import {API} from '../../api.js';
import {getAuthHeader, handleErrors} from '../../helpers/helpers.js';

const baseUrl = API.url + '/auth';

const TYPE_PREFIX = 'AUTH';

const types = {
  AUTH_LOGIN: `${TYPE_PREFIX}_LOGIN`,
  AUTH_LOGIN_SUCCESS: `${TYPE_PREFIX}_LOGIN_SUCCESS`,
  AUTH_LOGIN_FAILED: `${TYPE_PREFIX}_LOGIN_FAILED`,

  AUTH_LOGOUT: `${TYPE_PREFIX}_LOGOUT`,
  AUTH_LOGOUT_SUCCESS: `${TYPE_PREFIX}_LOGOUT_SUCCESS`,
  AUTH_LOGOUT_FAILED: `${TYPE_PREFIX}_LOGOUT_FAILED`,

  AUTH_CLEAR_SESSIONS: `${TYPE_PREFIX}_CLEAR_SESSIONS`,
  AUTH_CLEAR_SESSIONS_SUCCESS: `${TYPE_PREFIX}_CLEAR_SESSIONS_SUCCESS`,
  AUTH_CLEAR_SESSIONS_FAILED: `${TYPE_PREFIX}_CLEAR_SESSIONS_FAILED`,

  AUTH_VALIDATE_COOKIES: `${TYPE_PREFIX}_VALIDATE_COOKIES`,
  AUTH_VALIDATE_COOKIES_SUCCESS: `${TYPE_PREFIX}_VALIDATE_COOKIES_SUCCESS`,
  AUTH_VALIDATE_COOKIES_FAILED: `${TYPE_PREFIX}_VALIDATE_COOKIES_FAILED`,

  AUTH_LOAD_ADMINS: `${TYPE_PREFIX}_LOAD_ADMINS`,
  AUTH_LOAD_ADMINS_SUCCESS: `${TYPE_PREFIX}_LOAD_ADMINS_SUCCESS`,
  AUTH_LOAD_ADMINS_FAILED: `${TYPE_PREFIX}_LOAD_ADMINS_FAILED`,

  AUTH_ADD_ADMIN: `${TYPE_PREFIX}_ADD_ADMIN`,
  AUTH_ADD_ADMIN_SUCCESS: `${TYPE_PREFIX}_ADD_ADMIN_SUCCESS`,
  AUTH_ADD_ADMIN_FAILED: `${TYPE_PREFIX}_ADD_ADMIN_FAILED`,

  AUTH_UPDATE_ADMIN: `${TYPE_PREFIX}_UPDATE_ADMIN`,
  AUTH_UPDATE_ADMIN_SUCCESS: `${TYPE_PREFIX}_UPDATE_ADMIN_SUCCESS`,
  AUTH_UPDATE_ADMIN_FAILED: `${TYPE_PREFIX}_UPDATE_ADMIN_FAILED`,

  AUTH_DELETE_ADMIN: `${TYPE_PREFIX}_DELETE_ADMIN`,
  AUTH_DELETE_ADMIN_SUCCESS: `${TYPE_PREFIX}_DELETE_ADMIN_SUCCESS`,
  AUTH_DELETE_ADMIN_FAILED: `${TYPE_PREFIX}_DELETE_ADMIN_FAILED`,
}

const state = {
  isLoading: false,
  isLoggedIn: false,
  isLoginError: false,
  auth: {
    uid: '',
    hash: '',
    expires:  '',
    isAdmin: false
  },
  rememberMe: false,
  admins: [],
};

const getters = {
  isLoadingAuth: state => state.isLoading,
  authState: state => state.auth,
  isLoggedIn: state => state.isLoggedIn,
  isLoginError: state => state.isLoginError,
  isAdmin: state => state.auth.isAdmin,
  rememberMe: state => state.rememberMe,
  requestAuth: state => { return { uid: state.auth.uid, hash: state.auth.hash } },
  getAllAdmins: state => state.admins,  
};

const actions = {
  login({ commit }, { uid, password }) {
    return new Promise((resolve, reject) => {
      commit(types.AUTH_LOGIN);

      const postdata = {
        uid: uid,
        password: password
      };

      fetch(baseUrl + '/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postdata)
      })
        .then(handleErrors)
        .then(data => data.json())
        .then(response => {
          commit(types.AUTH_LOGIN_SUCCESS, response);
          delete response.isAdmin; delete response.name;
          resolve(response);
        })
        .catch(err => {
          console.error(err);
          commit(types.AUTH_LOGIN_FAILED);
          reject(err);
        });
    });
  },

  logout({ commit, state }, data) {
    return new Promise((resolve, reject) => {
      commit(types.AUTH_LOGOUT);

      fetch(baseUrl + '/logout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
        body: JSON.stringify(data)
      })
        .then(handleErrors)
        .then(data => data.json())
        .then(response => {
          commit(types.AUTH_LOGOUT_SUCCESS, response)
          resolve();
        })
        .catch(err => {
          console.error(err);
          commit(types.AUTH_LOGOUT_FAILED, state);
          reject();
        });
    });
  },

  clearSessions({ commit, state }) {
    const admins = [...state.admins];
    commit(types.AUTH_CLEAR_SESSIONS, admin);

    fetch(baseUrl + '/clearSessions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getAuthHeader()
      },
    })
      .then(handleErrors)
      .then(data => data.json())
      .then(response => commit(types.AUTH_CLEAR_SESSIONS_SUCCESS, response))
      .catch(err => {
        console.error(err);
        commit(types.AUTH_CLEAR_SESSIONS_FAILED, admins);
      });
  },

  validateCookies({ commit }, data) {
    return new Promise((resolve, reject) => {
      commit(types.AUTH_VALIDATE_COOKIES);

      fetch(baseUrl + '/validateCookies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      })
        .then(handleErrors)
        .then(data => data.json())
        .then(response => {
          commit(types.AUTH_VALIDATE_COOKIES_SUCCESS, response)
          resolve();
        })
        .catch(err => {
          console.error(err);
          commit(types.AUTH_VALIDATE_COOKIES_FAILED, state);
          reject();
        });
    });
  },

  setRememberMe({ commit }, bool) {
    commit('setRememberMe', bool);
  },

  loadAllAdmins({ commit }) {
    return new Promise((resolve, reject) => {
      commit(types.AUTH_LOAD_ADMINS);

      fetch(baseUrl + '/admins', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getAuthHeader()
        },
      })
        .then(handleErrors)
        .then(data => data.json())
        .then(response => {
          commit(types.AUTH_LOAD_ADMINS_SUCCESS, response)
          resolve();
        })
        .catch(err => {
          console.error(err);
          commit(types.AUTH_LOAD_ADMINS_FAILED);
          reject();
        });
    });
  },

  addAdmin({ commit, state }, admin) {
    const admins = [...state.admins];
    commit(types.AUTH_ADD_ADMIN, admin);

    fetch(baseUrl + '/addAdmin', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getAuthHeader()
      },
      body: JSON.stringify(admin)
    })
      .then(handleErrors)
      .then(data => data.json())
      .then(response => commit(types.AUTH_ADD_ADMIN_SUCCESS, response))
      .catch(err => {
        console.error(err);
        commit(types.AUTH_ADD_ADMIN_FAILED, admins);
      });
  },

  updateAdmin({ commit, state}, admin) {
    const admins = [...state.admins];
    commit(types.AUTH_UPDATE_ADMIN, admin);

    fetch(baseUrl + `/${admin.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getAuthHeader()
      },
      body: JSON.stringify(admin)
    })
      .then(handleErrors)
      .then(data => data.json())
      .then(response => commit(types.AUTH_UPDATE_ADMIN_SUCCESS, response))
      .catch(err => {
        console.error(err);
        commit(types.AUTH_UPDATE_ADMIN_FAILED, admins);
      });
  },

  deleteAdmin({ commit, state }, id) {
    const admins = [...state.admins];
    commit(types.AUTH_DELETE_ADMIN, { id });

    fetch(baseUrl + `/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': getAuthHeader()
      },
    })
      .then(handleErrors)
      .then(response => commit(types.AUTH_DELETE_ADMIN_SUCCESS, response))
      .catch(err => {
        console.error(err);
        commit(type.AUTH_DELETE_ADMIN_FAILED, admins);
      });
  },
};

const mutations = {
  loadingState(state, { isLoading }) {
    state.isLoading = isLoading;
  },

  [types.AUTH_LOGIN] (state) {
    state.isLoading = true;
  },
  [types.AUTH_LOGIN_SUCCESS] (state, res) {
    state.auth = res;
    state.isLoggedIn = true;
    state.isLoading = false;
    // set cookies if remember me is on
  },
  [types.AUTH_LOGIN_FAILED] (state) {
    state.isLoginError = true;
    state.isLoading = false;
  },

  [types.AUTH_LOGOUT] (state) {
    state.isLoading = true;
  },
  [types.AUTH_LOGOUT_SUCCESS] (state, res) {
    state.auth = {};
    state.isLoggedIn = false;    
    state.isLoading = false;
    // clear cookies
  },
  [types.AUTH_LOGOUT_FAILED] (state, oldState) {
    state = oldState;
    state.isLoading = false;
  },

  [types.AUTH_CLEAR_SESSIONS] (state) {
    state.isLoading = true;
  },
  [types.AUTH_CLEAR_SESSIONS_SUCCESS] (state, res) {
    state.auth = {};
    state.isLoggedIn = false;    
    state.isLoading = false;
    // clear cookies
  },
  [types.AUTH_CLEAR_SESSIONS_FAILED] (state, oldState) {
    state = oldState;
    state.isLoading = false;
  },

  [types.AUTH_VALIDATE_COOKIES] (state) {
    state.isLoading = true;
  },
  [types.AUTH_VALIDATE_COOKIES_SUCCESS] (state, res) {
    state.auth = res;
    state.isLoggedIn = true;
    state.isLoading = false;
  },
  [types.AUTH_VALIDATE_COOKIES_FAILED] (state, oldState) {
    state.isLoading = false;
  },

  setRememberMe(state, bool) {
    state.rememberMe = bool;
  },

  [types.AUTH_LOAD_ADMINS] (state) {
    state.isLoading = true;
  },
  [types.AUTH_LOAD_ADMINS_SUCCESS] (state, admins) {
    state.admins = admins;
    state.isLoading = false;
  },
  [types.AUTH_LOAD_ADMINS_FAILED] (state) {
    state.isLoading = false;
  },

  [types.AUTH_ADD_ADMIN] (state, admin) {
    state.admins = [
      ...state.admins,
      admin
    ];
  },
  [types.AUTH_ADD_ADMIN_SUCCESS] (state, response) {
    state.admins.pop();
    state.admins = [
      ...state.admins,
      response
    ];
  },
  [types.AUTH_ADD_ADMIN_FAILED] (state, admins) {
    state.admins = admins;
  },

  [types.AUTH_UPDATE_ADMIN] (state, admin) {

  },
  [types.AUTH_UPDATE_ADMIN_SUCCESS] (state, response) {
    // console.log(response);
  },
  [types.AUTH_UPDATE_ADMIN_FAILED] (state, admins) {
    state.admins = admins;
  },

  [types.AUTH_DELETE_ADMIN] (state, { id }) {
    state.admins = state.admins.filter(admin => admin.id != id);
  },
  [types.AUTH_DELETE_ADMIN_SUCCESS] (state, response) {
    // console.log(response);
  },
  [types.AUTH_DELETE_ADMIN_FAILED] (state, admins) {
    state.admins = admins;
  },
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/auth.js